import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { AllUsersApiService } from 'app/library/all-users/services/all-users.api-service';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { TeamService } from 'app/library/team/services/team.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { GlobalParticipant } from 'app/projects/user/src/lib/models/participant.model';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-certificates-team-members-filter',
    templateUrl: './certificates-team-members-filter.component.html',
    styleUrls: ['./certificates-team-members-filter.component.scss'],
    animations: fuseAnimations,
    encapsulation: ViewEncapsulation.None,
})
export class CertificatesTeamMembersFilterComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    teamMembers$: Observable<GlobalParticipant[]>;

    @Input()
    teamId: string;

    @Input()
    includeSubteams: boolean;

    @Output()
    addedToList = new EventEmitter<IChoosenFilterInterface[]>();

    dataSource: MatTableDataSource<GlobalParticipant>;
    displayedColumns = [];
    searchInput = new FormControl();
    hideEmail: boolean;
    list: IChoosenFilterInterface[] = [];
    hasUsers = false;

    @ViewChild(MatSort)
    sort: MatSort;

    sortBy: string;
    sortOrder: SortDirection;

    private _updateDataSource(): void {
        this.teamMembers$
            .pipe(
                map((members) =>
                    members.map((member) => ({
                        ...member,
                        alreadyMember: this.list.some((item) => item.entity.id === member.id),
                    }))
                ),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((mappedUsers) => {
                this.hasUsers = !!mappedUsers?.length;
                this.dataSource = new MatTableDataSource(mappedUsers);
                this.dataSource.sort = this.sort;
            });
    }

    constructor(
        private _allUsersService: AllUsersApiService,
        private _translateService: TranslateService,
        private _featureService: FeatureService,
        private _teamService: TeamService
    ) {
        super();

        this.hideEmail = this._featureService.checkFeatureStatus(FeatureSwitchName.ParticipantEmailVisibility, 'inactive');
    }

    ngOnInit(): void {
        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(1000), distinctUntilChanged()).subscribe(() => {
            this.loadMembersPage();
        });

        this._allUsersService.filtersList$.pipe(takeUntil(this._unsubscribeAll)).subscribe((filters) => {
            this.list = filters;
            this._updateDataSource();
        });

        setTimeout(() => {
            if (this.sortBy && this.sortOrder) {
                this.sort.sort({
                    id: this.sortBy || '',
                    start: this.sortOrder || 'asc',
                    disableClear: false,
                });
            }

            this.loadMembersPage();

            if (this.hideEmail) {
                this.displayedColumns = ['avatar', 'firstName', 'lastName', 'buttons'];
            } else {
                this.displayedColumns = ['avatar', 'firstName', 'lastName', 'email', 'buttons'];
            }
        });
    }

    addToList(user: GlobalParticipant): void {
        this.addedToList.emit([
            ...this.list,
            {
                entity: { id: user.id, type: 'TeamMembers' },
                valueToShow: `${this._translateService.instant('USER.PLURAL')}: ${user.fullName}`,
            },
        ]);
    }

    removeFromList(user: GlobalParticipant): void {
        this.addedToList.emit(this.list.filter((item) => !(item.entity.id === user.id && item.entity.type === 'TeamMembers')));
    }

    async loadMembersPage(): Promise<void> {
        if (this.dataSource) {
            this._teamService.fetchTeamMembersUsers(this.teamId, this.includeSubteams);
        }
    }
}
